import { useEffect, useRef, useState } from 'react';
import React from 'react';

import useRect from '@lyearn/core/hooks/useRect';
import useScreen from '@lyearn/core/hooks/useScreen';
import { ScreenTypeEnum } from '@lyearn/core/hooks/useScreen/types';
import useToggle from '@lyearn/core/hooks/useToggle';
import ErrorReporter from '@lyearn/core/utils/ErrorReporter';
import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { css } from '@lyearn/style';
import { Loader, Stack } from '@lyearn/ui';
import useGetAIResponse from '@/components/HelpMenu/hooks/useGetAIResponse';
import * as BaseMessageList from '@/features/messages/components/MessageList';
import { ChannelFragment } from '@/features/messages/graphql/fragments/generated/Channel';
import { ChatFragment } from '@/features/messages/graphql/fragments/generated/Chat';
import { useCreatePostMutation } from '@/features/messages/graphql/mutations/generated/createPost';
import { useChannelsSubscription } from '@/features/messages/graphql/subscriptions/generated/Channel';
import { useChannelPostsSubscription } from '@/features/messages/graphql/subscriptions/generated/ChannelPosts';
import { useAppConfig } from '@/hooks/useAppConfig';
import {
  ChannelAccessTypeEnum,
  ChannelParentEntityTypeEnum,
  ChannelPostsSubscriptionOperation,
  ChannelTypeEnum,
  CreatePostMeta,
  PostTypeEnum,
} from '@/types/schema';

import { verticalDivider } from '../corporate/modules/home/views/Home/components/HomepageCalendar';
import ChatHeader, { MobileTabs } from './components/ChatHeader';
import { ChatInput } from './components/ChatInput';
import ContentView from './components/ContentView';
import { InitialChatInput } from './components/InitialChatInput';
import { KnowledgeBaseSidebar } from './components/KnowledgeBaseSidebar';
import MessageList from './components/MessageList';
import MyAIChannels from './components/MyAIChannels';
import NotesSection from './components/NotesSection';
import { useCreateChannelMutation } from './graphql/mutations/generated/createChannel';
import { NoteFragment } from './graphql/queries/generated/notes';
import { flexN, height100vh, height100vhMinusHeader } from './styles';
import { ChatbotMessageType, PinnedContentType, ViewMode } from './types';
import { convertMessageToUserMessage, getNewObjectId, replaceMentionsWithSyntax } from './utils';

type Props = {
  onClose: () => void;
  startInTeachMode?: boolean;
};

const i18n = {
  deleteCommentTitle: 'Channel:DeleteMessageTitle',
  deleteCommentDescription: 'Channel:DeleteMessageDescription',
};

const MAX_MOBILE_WIDTH = 767;

const hideScrollbar = css`
  ::-webkit-scrollbar {
    width: 1em;
  }
`;

const VerticalDivider = () => (
  <div className={cn(verticalDivider, 'bg-interactive-secondary-hover')} />
);

const KnowledgeBaseCopilot = (props: Props) => {
  const { onClose, startInTeachMode = false } = props;
  const { t } = useTranslation(['Channel']);
  const [teachModeEnabled, { toggle: toggleTeachMode }] = useToggle(startInTeachMode);
  const [message, setMessage] = useState('');
  const [currentNote, setCurrentNote] = useState<NoteFragment | null>(null);
  const [hideNoteButtons, setHideNoteButtons] = useState(false);
  const [currentChannel, setCurrentChannel] = useState<ChatFragment | null>(null);
  const [currentContent, setCurrentContent] = useState<PinnedContentType | null>(null);
  const [followUpQuestions, setFollowUpQuestions] = useState<Record<string, string[]>>({});
  const [isFreshChat, setIsFreshChat] = useState(true);
  const [updatedTitle, setUpdatedTitle] = useState<string | undefined>(undefined);
  const [channelTitle, setChannelTitle] = useState('');
  const [initialLoading, setInitialLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState<MobileTabs>(MobileTabs.Chat); //mobile only
  const [showListDrawer, setShowListDrawer] = useState(false); //mobile only
  const [isWaiting, setIsWaiting] = useState(false);
  const [userMessage, setUserMessage] = useState<ChatbotMessageType | null>(null);
  const [error, setError] = useState<boolean>(false);
  const [{ fetching }, createChannel] = useCreateChannelMutation();
  const [, createMessage] = useCreatePostMutation();
  const { ref, width } = useRect<HTMLDivElement>();
  const [generativeTaskId, setGenerativeTaskId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.Chat);
  const screen = (width ?? 0) > MAX_MOBILE_WIDTH ? ScreenTypeEnum.Desktop : ScreenTypeEnum.Mobile;
  const { workspaceId } = useAppConfig();
  const [lastSentIds, setLastSentIds] = useState<{
    postId: string | undefined;
    thinkingPostId: string | undefined;
    assistantPostId: string | undefined;
  }>({
    postId: undefined,
    thinkingPostId: undefined,
    assistantPostId: undefined,
  });
  const { aiResponse, isSubscribing, setAIResponse } = useGetAIResponse({
    generativeTaskId_: generativeTaskId,
    channelId: currentChannel?.id,
    setIsWaiting,
    channelTitle,
    setChannelTitle,
    followUpQuestions,
    setFollowUpQuestions,
    isFreshChat,
    setIsFreshChat,
    setError,
  });
  const { isMobile } = useScreen();

  // Add useEffect to handle window resize
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const theGreatReset = () => {
    setMessage('');
    setCurrentChannel(null);
    setCurrentContent(null);
    // setCurrentNote(null);
    // notes has been moved out to another column so we won't reset it for now
    setUpdatedTitle('');
    setChannelTitle('');
    setUserMessage(null);
    setGenerativeTaskId(null);
    setIsWaiting(false);
    setError(false);
    setAIResponse(null);
  };

  // triggered only once- when the user asks their first question
  const onSubmit = (newUserMessage: ChatbotMessageType) => {
    setInitialLoading(true);
    setIsWaiting(true);
    setChannelTitle(t('Common:KnowledgeBase.NewChat'));
    createChannel({
      data: {
        name: t('Common:KnowledgeBase.NewChat'),
        accessType: ChannelAccessTypeEnum.Public,
        parentId: workspaceId!,
        type: teachModeEnabled ? ChannelTypeEnum.CoachTheCoach : ChannelTypeEnum.ChatWithAi,
        parentType: ChannelParentEntityTypeEnum.Workspace,
      },
    })
      .then((data) => {
        if (data?.data?.createChannel?.channel) {
          const channelId = data.data.createChannel.channel.id;
          setCurrentChannel(data.data.createChannel.channel as ChatFragment);
          setInitialLoading(false);

          const postId = newUserMessage.id;
          const thinkingPostId = getNewObjectId();
          const assistantPostId = getNewObjectId();
          setLastSentIds({
            postId,
            thinkingPostId,
            assistantPostId,
          });

          createMessage({
            post: {
              channelId,
              message: newUserMessage.textContent,
              type: PostTypeEnum.Post,
              messageText: replaceMentionsWithSyntax(newUserMessage.textContent),
              postId,
              thinkingPostId,
              assistantPostId,
            },
          })
            .then((data) => {
              if (data.data?.createPost?.generativeTaskId) {
                setGenerativeTaskId(data.data.createPost.generativeTaskId);
              } else {
                setError(true);
                ErrorReporter.error(
                  new Error(
                    `Didn't receive generativeTaskId from API. channelId: <${channelId}> | FE-generated postId: <${
                      newUserMessage.id
                    }> | message: <${newUserMessage.textContent}> | API response: <${JSON.stringify(
                      data,
                    )}>`,
                  ),
                );
              }
              setMessage('');
            })
            .catch((error) => {
              setError(true);
              ErrorReporter.error(
                new Error(
                  `Failed to create message. channelId: <${channelId}> | FE-generated postId: <${newUserMessage.id}> | message: <${newUserMessage.textContent}> | error: <${error}>`,
                ),
              );
            });
        }
      })
      .catch((error) => {
        setError(true);
        ErrorReporter.error(new Error(`Failed to create channel. error: <${error}>`));
      });
  };

  const onHeaderAction = (value: ViewMode) => {
    switch (value) {
      case ViewMode.Chat:
        {
          setViewMode(ViewMode.Chat);
          setCurrentChannel(null);
          setChannelTitle(t('Common:KnowledgeBase.NewChat'));
          setIsFreshChat(true);
          setShowListDrawer(false);
        }
        break;
      case ViewMode.ChatHistory:
        {
          setViewMode(ViewMode.ChatHistory);
          setCurrentChannel(null);
        }
        break;
    }
  };

  // handles message creation
  const onSubmitMessage = (newUserMessage: ChatbotMessageType, meta?: CreatePostMeta) => {
    if (!currentChannel) return;
    setIsWaiting(true);
    setMessage('');

    const postId = newUserMessage.id;
    const thinkingPostId = getNewObjectId();
    const assistantPostId = getNewObjectId();
    setLastSentIds({
      postId,
      thinkingPostId,
      assistantPostId,
    });

    createMessage({
      post: {
        channelId: currentChannel?.id,
        message: newUserMessage.textContent,
        type: PostTypeEnum.Post,
        messageText: replaceMentionsWithSyntax(newUserMessage.textContent),
        meta,
        postId,
        thinkingPostId,
        assistantPostId,
      },
    })
      .then((data) => {
        if (data.data?.createPost?.generativeTaskId) {
          setGenerativeTaskId(data.data.createPost.generativeTaskId);
        } else {
          setError(true);

          ErrorReporter.error(
            new Error(
              `Didn't receive generativeTaskId from API. channelId: <${currentChannel?.id}> | FE-generated postId: <${
                newUserMessage.id
              }> | message: <${newUserMessage.textContent}> | API response: <${JSON.stringify(
                data,
              )}>`,
            ),
          );
        }
      })
      .catch((error) => {
        setError(true);
        ErrorReporter.error(
          new Error(
            `Failed to create message. channelId: <${currentChannel?.id}> | FE-generated postId: <${newUserMessage.id}> | message: <${newUserMessage.textContent}> | error: <${error}>`,
          ),
        );
      });
  };

  // user presses enter to submit their FIRST QUESTION
  // handles channel creation and message creation
  const handleFirstEnter = (text: string) => {
    const newUserMessage = convertMessageToUserMessage(text);
    setUserMessage(newUserMessage);
    setIsWaiting(true);
    setMessage('');

    setTimeout(() => {
      onSubmit(newUserMessage);
    }, 0);
  };

  // handles messages from the textbox after the inital question has been sent
  // only handles message creation
  const handleEnter = (text: string) => {
    const newUserMessage = convertMessageToUserMessage(text);
    setUserMessage(newUserMessage);
    setIsWaiting(true);
    setMessage('');

    setTimeout(() => {
      onSubmitMessage(newUserMessage);
    }, 0);
  };

  const onClickStarterQuestion = (question: string) => {
    const newUserMessage = convertMessageToUserMessage(question);
    setUserMessage(newUserMessage);
    setIsWaiting(true);

    setTimeout(() => {
      onSubmit(newUserMessage);
    }, 0);
  };

  const onClickSend = (firstMessage: boolean) => {
    if (isWaiting || !message.trim().length) {
      return;
    }
    const newUserMessage = convertMessageToUserMessage(message);
    setUserMessage(newUserMessage);
    setIsWaiting(true);

    setTimeout(() => {
      if (firstMessage) {
        onSubmit(newUserMessage);
      } else {
        onSubmitMessage(newUserMessage);
      }
    }, 0);
  };

  const onClickFollowUp = (question: string, meta?: CreatePostMeta) => {
    setMessage(question);
    const newUserMessage = convertMessageToUserMessage(question);
    setUserMessage(newUserMessage);
    setIsWaiting(true);

    setTimeout(() => {
      onSubmitMessage(newUserMessage, meta);
    }, 0);
  };

  useChannelPostsSubscription({
    variables: {
      input: {
        channelIds: [currentChannel?.id || ''],
        types: [PostTypeEnum.Post],
        operations: [
          ChannelPostsSubscriptionOperation.PostCreated,
          ChannelPostsSubscriptionOperation.PostUpdated,
          ChannelPostsSubscriptionOperation.PostDeleted,
        ],
      },
    },
    pause: !currentChannel?.id.length,
  });

  useChannelsSubscription({
    variables: {
      data: {
        channelIds: [currentChannel?.id || ''],
      },
    },
    pause: !currentChannel?.id.length,
  });

  if (viewMode === ViewMode.ChatHistory) {
    return (
      <MyAIChannels
        setCurrentChannel={setCurrentChannel}
        setViewMode={setViewMode}
        onClose={onClose}
      />
    );
  }

  const renderView = () => {
    if (isMobile && currentTab === MobileTabs.Notes) {
      return null;
    }
    switch (viewMode) {
      case ViewMode.Chat:
        return (
          <>
            {currentChannel ? (
              <BaseMessageList.Provider
                key={currentChannel.id}
                pauseSubscription
                channel={currentChannel as ChannelFragment}
                i18n={i18n}>
                <MessageList
                  aiResponse={aiResponse}
                  channel={currentChannel as ChannelFragment}
                  error={error}
                  followUpQuestions={followUpQuestions[currentChannel.id] || []}
                  generativeTaskId={generativeTaskId}
                  isGeneratingAIResponse={isSubscribing}
                  isWaiting={isWaiting}
                  lastSentIds={lastSentIds}
                  screen={screen}
                  setCurrentNote={setCurrentNote}
                  setError={setError}
                  setGenerativeTaskId={setGenerativeTaskId}
                  setHideNoteButtons={setHideNoteButtons}
                  setIsWaiting={setIsWaiting}
                  teachModeEnabled={teachModeEnabled}
                  userMessage={userMessage}
                  onClickFollowUp={onClickFollowUp}
                />
              </BaseMessageList.Provider>
            ) : (
              <InitialChatInput
                message={message}
                teachModeEnabled={teachModeEnabled}
                toggleTeachMode={toggleTeachMode}
                onClickSend={onClickSend}
                onClickStarterQuestion={onClickStarterQuestion}
                onEnter={handleFirstEnter}
                onMessageChange={setMessage}
              />
            )}
          </>
        );
      case ViewMode.Content:
        return currentContent ? (
          <ContentView key={currentContent.id} content={currentContent} />
        ) : null;
    }
  };

  return (
    <Stack className={cn(height100vh, 'h-full w-full')} direction="col">
      <ChatHeader
        channelTitle={channelTitle}
        currentTab={currentTab}
        note={currentNote}
        setCurrentTab={setCurrentTab}
        setShowListDrawer={setShowListDrawer}
        setViewMode={setViewMode}
        updatedTitle={updatedTitle}
        viewMode={viewMode}
        onAction={onHeaderAction}
        onClose={onClose}
      />
      <Stack className={cn(height100vhMinusHeader, 'h-full w-full')}>
        <KnowledgeBaseSidebar
          currentChannel={currentChannel}
          currentContent={currentContent}
          currentNote={currentNote}
          setChannelTitle={setChannelTitle}
          setCurrentChannel={setCurrentChannel}
          setCurrentContent={setCurrentContent}
          setCurrentNote={setCurrentNote}
          setIsFreshChat={setIsFreshChat}
          setShowListDrawer={setShowListDrawer}
          setUpdatedTitle={setUpdatedTitle}
          setViewMode={setViewMode}
          showListDrawer={showListDrawer}
          teachModeEnabled={teachModeEnabled}
          theGreatReset={theGreatReset}
          onNewChat={() => onHeaderAction(ViewMode.Chat)}
        />
        <VerticalDivider />
        <Stack className={cn('w-full', height100vhMinusHeader)} direction="row">
          {isMobile && currentTab === MobileTabs.Notes ? null : (
            <Stack
              ref={ref}
              className={cn(height100vhMinusHeader, 'w-full', hideScrollbar, flexN(3))}
              direction="col"
              justifyContent={currentChannel ? 'between' : 'start'}>
              <Stack
                className="w-full overflow-hidden relative flex-1"
                direction="col"
                justifyContent={currentChannel && !currentNote ? 'between' : 'start'}>
                {fetching || initialLoading ? (
                  // Might have to annihilate this state
                  <Stack className="!m-auto">
                    <Loader size={20} />
                  </Stack>
                ) : (
                  renderView()
                )}
              </Stack>
              {currentChannel && viewMode === ViewMode.Chat ? (
                <ChatInput
                  clearChannel={() => setCurrentChannel(null)}
                  error={error}
                  isWaiting={isWaiting}
                  message={message}
                  teachModeEnabled={teachModeEnabled}
                  toggleTeachMode={toggleTeachMode}
                  onClickSend={onClickSend}
                  onEnter={handleEnter}
                  onMessageChange={(value) => setMessage(value)}
                />
              ) : null}
            </Stack>
          )}
          <VerticalDivider />
          <NotesSection
            currentNote={currentNote}
            currentTab={currentTab}
            hideNoteButtons={hideNoteButtons}
            setCurrentNote={setCurrentNote}
            setHideNoteButtons={setHideNoteButtons}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default React.memo(KnowledgeBaseCopilot);
